import React, { useRef, useState, useMemo } from 'react'
import { Row, Column, Tabs, TabHeader, TabsContents, TabsHeaders, GridContainer, Typography, ResponsiveImage, Section, H2 } from '@vp/swan'

import StockOptionContent from './StockOptionContent'
import { useAppContext } from '../../state/AppContext'

import { stylesheet } from './StockOptionSection.scss'
import { buildStockOptionData, MpvIdsData, reorderOutOfStockItems } from './StockOptionUtil'
import { BFFPageData } from '../../services/serviceClients/BffClient'
import { trackTabSelected } from '../../utils/tracking/tracking'
import { useStyles } from '@vp/ubik-context'
import { formatLocale } from '../../utils/buildHelpers'

const StockOptions = (props: { pageDataFromBFF: BFFPageData }) => {
  useStyles(stylesheet)
  const ref = useRef<HTMLDivElement>(null)
  const { pageDataFromBFF } = props
  const { merchandisingCategoryId, locale, enableCampaignCallouts } = useAppContext()

  const stockOptionData = useMemo(() => {
    return buildStockOptionData(pageDataFromBFF, formatLocale(locale, true))
  }, [])
  let reorderedData: [string, MpvIdsData[keyof MpvIdsData]][] | null = null

  const [selectedTabId, setSelectedTabId] = useState<string | null>('1')

  const handleTabChange = (tabId: string | null) => {
    if (tabId) {
      setSelectedTabId(tabId)
      sessionStorage.setItem('stockOptionsTabId', tabId)
    }
  }

  if (pageDataFromBFF && stockOptionData?.mpvIds) {
    reorderedData = useMemo(() => reorderOutOfStockItems(pageDataFromBFF, Object.entries(stockOptionData?.mpvIds)), [])
  }

  if (!stockOptionData) return null

  return (
    <GridContainer paddingX={0}>
      <Section marginBottom={3}>
        <H2 data-testid="sectionTitle" fontSize="x2large">
          {stockOptionData.sectionTitle}
        </H2>
        <Typography data-testid="sectionDescription" fontSize="standard">
          {stockOptionData.sectionDescription}
        </Typography>
      </Section>
      <Row>
        <Column span={12}>
          <Tabs
            data-testid="stockOptionsTabs"
            selectedTabId={selectedTabId}
            onRequestTabChange={(requestedTabId) => {
              trackTabSelected({
                categoryId: merchandisingCategoryId,
                pageZone: 'stockOptions',
                tabIndex: requestedTabId,
              })
              handleTabChange(requestedTabId)
            }}
          >
            <TabsHeaders ref={ref} data-testid="stockOptionsTabsHeaders">
              {reorderedData?.map(([mpvId, item], tabIndex) => {
                return (
                  <TabHeader tabId={`${tabIndex + 1}`} key={mpvId} data-testid="stockOptionsTabItem">
                    <ResponsiveImage
                      data-testid={`stockOptionsTabImage-${item.merchandisingName}`}
                      className="tabimage-border"
                      src={item.tabImageUrl ?? item.imageUrl}
                      width="64"
                      height="64"
                      alt={`${item.merchandisingName}`}
                    />
                    <br />
                    <Typography fontSize="small" data-testid="merchandisingName">
                      {item.merchandisingName}
                    </Typography>
                  </TabHeader>
                )
              })}
            </TabsHeaders>
            <TabsContents data-testid="stockOptionsTabsContents">
              {stockOptionData &&
                reorderedData?.map(([mpvId, item], tabIndex: number) => {
                  const currentTabId = `${tabIndex + 1}`
                  const isActiveTab = selectedTabId === currentTabId
                  return (
                    <StockOptionContent
                      key={mpvId}
                      mpvId={mpvId}
                      item={item}
                      browseDesign={item.browseDesign!}
                      browseDesignCta={stockOptionData.browseDesignCta}
                      uploadDesignCta={stockOptionData.uploadDesignCta}
                      viewDetailsCta={stockOptionData.viewDetailsCta}
                      highlightsSectionTitle={stockOptionData.highlightsSectionTitle}
                      optionsSectionTitle={stockOptionData.optionsSectionTitle}
                      tabIndex={tabIndex}
                      enableCampaignCallouts={enableCampaignCallouts}
                      outOfStockContent={stockOptionData.outOfStockContent}
                      pageDataFromBFF={pageDataFromBFF}
                      isActiveTab={isActiveTab}
                      currentTabId={currentTabId}
                    />
                  )
                })}
            </TabsContents>
          </Tabs>
        </Column>
      </Row>
    </GridContainer>
  )
}

export default StockOptions
